// src/hooks/useReviewerLogicQuizOnly.js
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getFolders, generateReviewer, saveReviewer } from "../services/api";
import { prepareReviewerPayload, prepareReviewerBatchPayloads } from "../services/examLogic";
import { processUploadedFiles, validateAcademicStudyMaterial } from "../utils/fileHelpers";
import { generatePDFDocument } from "../utils/pdfGenerator";
import { useAuth } from "../contexts/AuthContext"; 

export const useReviewerLogicQuizOnly = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // State
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [view, setView] = useState("form");
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingText, setLoadingText] = useState("Initializing...");

  // Form Data
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [textInput, setTextInput] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [rawContent, setRawContent] = useState("");
  const [generatedData, setGeneratedData] = useState(null);
  const MAX_EXAM_NAME_LENGTH = 30;

  // ✅ QUIZ ONLY CONFIG
  const [aiConfig, setAiConfig] = useState({
    summaryEnabled: false,
    flashcardsEnabled: false,
    quizEnabled: true,
    difficulty: 2,
    quizType: ["mcq"],
    questionCount: 10,
    flashcardCount: 0,
    timePerQuestion: 30,
    customInstructions: "",
    eli5: false,
    batchEnabled: true,
    maxCharsPerBatch: 24000,
    overlapChars: 1000,
    totalQuestionsMode: true,
    totalFlashcardsMode: false,
    maxQuestionsPerBatch: 60,
    maxFlashcardsPerBatch: 0,
    autoBatchThresholdChars: 18000,
    targetMaxBatches: 24,
    maxMaxCharsPerBatch: 32000,
    throttleMs: 1200,
    maxRetries: 5,
  });

  // Auth Redirect Guard
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  // Load Folders (Using Cookie Auth)
  useEffect(() => {
    if (!user) return;

    const fetchFolders = async () => {
      setFoldersLoading(true);
      try {
        const res = await getFolders();
        let list = [];
        if (Array.isArray(res)) list = res;
        else if (Array.isArray(res?.folders)) list = res.folders;
        else if (Array.isArray(res?.data)) list = res.data;
        else if (Array.isArray(res?.data?.folders)) list = res.data.folders;

        setFolders(list);
      } catch (err) {
        console.error("Folder fetch error", err);
        setFolders([]);
      } finally {
        setFoldersLoading(false);
      }
    };
    fetchFolders();
  }, [user]);

  const updateConfig = (key, val) => setAiConfig((prev) => ({ ...prev, [key]: val }));

  // Helpers
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const normalizeQuizText = (v) =>
    String(v ?? "")
      // Keep escaped currency symbols as real currency.
      .replace(/\\\$/g, "$")
      // Convert common LaTeX text blocks to plain text.
      .replace(/\\text\s*\{([^}]*)\}/g, "$1")
      // Common LaTeX math operators/symbols.
      .replace(/\\times/g, "×")
      .replace(/\\div/g, "÷")
      .replace(/\\cdot/g, "·")
      .replace(/\\pm/g, "±")
      .replace(/\\geq|\\ge/g, ">=")
      .replace(/\\leq|\\le/g, "<=")
      .replace(/\\neq/g, "!=")
      .replace(/\\left|\\right/g, "")
      // Basic fraction/square-root rendering.
      .replace(/\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, "($1)/($2)")
      .replace(/\\sqrt\s*\{([^{}]+)\}/g, "sqrt($1)")
      // Unwrap inline/display math delimiters but keep inner content.
      .replace(/\$([^$]+)\$/g, "$1")
      .replace(/\\%/g, "%")
      .replace(/\\\(/g, "")
      .replace(/\\\)/g, "")
      .replace(/\\\[/g, "")
      .replace(/\\\]/g, "")
      // Remove stray escape slash before digits (e.g., "\12,500" -> "12,500").
      .replace(/\\(?=\d)/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const normalizeQuizItems = (quizItems) =>
    (Array.isArray(quizItems) ? quizItems : []).map((q) => {
      const question = normalizeQuizText(q?.question);
      let options = Array.isArray(q?.options) ? q.options.map(normalizeQuizText) : [];
      let answer = normalizeQuizText(q?.answer);
      const normalizeCmp = (s) =>
        String(s || "")
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();

      // Remove leading option labels accidentally generated by AI (e.g., "A. ", "B) ").
      options = options.map((opt) => opt.replace(/^[A-Da-d]\s*[\.\):-]\s*/, "").trim());
      answer = answer.replace(/^[A-Da-d]\s*[\.\):-]\s*/, "").trim();

      // If all options share the same variable assignment prefix (e.g., "x = "), trim it.
      const varPrefixPattern = /^([a-zA-Z])\s*=\s*/;
      const prefixes = options
        .map((opt) => opt.match(varPrefixPattern)?.[0] || null)
        .filter(Boolean);
      const sharedPrefix =
        options.length > 0 && prefixes.length === options.length && prefixes.every((p) => p === prefixes[0])
          ? prefixes[0]
          : null;

      if (sharedPrefix) {
        const esc = sharedPrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const re = new RegExp(`^${esc}`);
        options = options.map((opt) => opt.replace(re, "").trim());
        answer = answer.replace(re, "").trim();
      }

      // Force answer to resolve to one exact option to avoid scoring mismatches.
      if (options.length > 0 && answer) {
        const letterMatch = answer.match(/^[A-H]$/i);
        const numberMatch = answer.match(/^\d+$/);

        if (letterMatch) {
          const idx = letterMatch[0].toUpperCase().charCodeAt(0) - 65;
          if (idx >= 0 && idx < options.length) answer = options[idx];
        } else if (numberMatch) {
          const idx = Number(numberMatch[0]) - 1;
          if (idx >= 0 && idx < options.length) answer = options[idx];
        } else {
          const normAnswer = normalizeCmp(answer);
          const exact = options.find((opt) => normalizeCmp(opt) === normAnswer);
          if (exact) {
            answer = exact;
          } else {
            const fuzzy = options.filter((opt) => {
              const n = normalizeCmp(opt);
              return n.includes(normAnswer) || normAnswer.includes(n);
            });
            if (fuzzy.length === 1) answer = fuzzy[0];
          }
        }
      }

      return {
        ...q,
        question,
        options,
        answer,
        explanation: normalizeQuizText(q?.explanation),
      };
    });

  const extractErrText = (err) => {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      String(err);
    return String(msg || "");
  };

  const extractStatus = (err) => err?.response?.status || err?.status || null;

  const isDailyQuotaExceeded = (errText) => {
    const t = String(errText || "").toLowerCase();
    return (
      t.includes("quota exceeded") ||
      t.includes("free_tier_requests") ||
      t.includes("per day") ||
      t.includes("all google api keys have failed or exhausted their quota")
    );
  };

  const isWindowLimiterExceeded = (errText) => {
    const t = String(errText || "").toLowerCase();
    return (
      t.includes("ai generation limit reached") ||
      t.includes("2 reviewers/exams every 3 hours") ||
      t.includes("every 3 hours")
    );
  };

  const isTempRateLimit = (status, errText) => {
    const t = String(errText || "").toLowerCase();
    return (
      status === 429 ||
      t.includes("too many requests") ||
      t.includes("rate limit") ||
      t.includes("resource_exhausted") ||
      t.includes("resource exhausted") ||
      t.includes("retry in")
    );
  };

  const isIncompleteOutputError = (status, errText) => {
    const t = String(errText || "").toLowerCase();
    return (
      status === 422 ||
      t.includes("output remained incomplete") ||
      t.includes("incomplete output") ||
      t.includes("summary too short")
    );
  };

  const parseRetryDelayMs = (err, errText) => {
    const retryAfterRaw =
      err?.response?.headers?.["retry-after"] ||
      err?.response?.headers?.["Retry-After"];
    if (retryAfterRaw != null) {
      const n = Number(retryAfterRaw);
      if (Number.isFinite(n) && n >= 0) return Math.round(n * 1000);
    }

    const t = String(errText || "");
    const m1 = t.match(/retry in\s+(\d+(?:\.\d+)?)s/i);
    if (m1?.[1]) return Math.max(0, Math.round(Number(m1[1]) * 1000));
    return null;
  };

  const validateStudyContent = (text) => {
    const raw = String(text || "").trim();
    if (!raw) return { isValid: false, message: "No content to process. Upload a file or paste text." };
    if (raw.length < 50) {
      return { isValid: false, message: "Content is too short. Please provide at least 50 characters." };
    }

    const compact = raw.replace(/\s+/g, "");
    if (/([A-Za-z0-9]{12,})\1{3,}/.test(compact)) {
      return {
        isValid: false,
        message: "Input looks repetitive or gibberish. Please paste readable study notes.",
      };
    }

    if (/([^\s])\1{10,}/.test(raw)) {
      return {
        isValid: false,
        message: "Input contains repeated characters and is not readable study content.",
      };
    }

    const words = raw.match(/[A-Za-z]{2,}/g) || [];
    if (words.length === 0) {
      return { isValid: false, message: "No readable words found. Please provide real study material." };
    }

    const uniqueRatio = new Set(words.map((w) => w.toLowerCase())).size / words.length;
    const longTokenRatio = words.filter((w) => w.length > 20).length / words.length;

    if (words.length >= 20 && uniqueRatio < 0.18) {
      return {
        isValid: false,
        message: "Input is too repetitive. Please provide clearer notes with meaningful sentences.",
      };
    }

    if (words.length >= 10 && longTokenRatio > 0.6) {
      return {
        isValid: false,
        message: "Input looks malformed (too many long random tokens). Please paste normal text.",
      };
    }

    return { isValid: true, message: "" };
  };

  const mergeBatchOutputs = (batchOutputs) => {
    const safeBatches = Array.isArray(batchOutputs) ? batchOutputs : [];
    const quiz = safeBatches
      .flatMap((b) => (Array.isArray(b?.quiz) ? b.quiz : []))
      .map((q) => ({
        question: String(q?.question ?? ""),
        options: Array.isArray(q?.options) ? q.options.map(String) : [],
        answer: String(q?.answer ?? ""),
        explanation: String(q?.explanation ?? ""),
      }));

    const firstAnalyticsTopic =
      safeBatches.find(
        (b) => typeof b?.analyticsTopic === "string" && b.analyticsTopic.trim()
      )?.analyticsTopic || null;

    return {
      summary: "",
      flashcards: [],
      quiz: normalizeQuizItems(quiz),
      batches: safeBatches,
      analyticsTopic: firstAnalyticsTopic,
    };
  };

  const trimMergedToTotals = (merged) => {
    const out = { ...merged };
    if (aiConfig.quizEnabled && aiConfig.totalQuestionsMode) {
      const totalQ = Math.max(0, Math.floor(Number(aiConfig.questionCount ?? 0)));
      if (Number.isFinite(totalQ) && totalQ >= 0) {
        out.quiz = Array.isArray(out.quiz) ? out.quiz.slice(0, totalQ) : [];
      }
    }
    out.summary = "";
    out.flashcards = [];
    return out;
  };

  const generateReviewerWithRetry = async (payload, attempt = 0) => {
    try {
      return await generateReviewer(payload);
    } catch (err) {
      const status = extractStatus(err);
      const errText = extractErrText(err);

      if (isDailyQuotaExceeded(errText)) {
        const e = new Error("Daily quota reached. Upgrade billing.");
        e.__dailyQuota = true;
        throw e;
      }

      if (isWindowLimiterExceeded(errText)) {
        const e = new Error("AI generation limit reached (2 generations per 3 hours).");
        e.__windowLimit = true;
        throw e;
      }

      const shouldRetry = isTempRateLimit(status, errText) && attempt < Number(aiConfig.maxRetries || 3);
      if (!shouldRetry) throw err;

      const parsedDelay = parseRetryDelayMs(err, errText);
      const jitter = Math.floor(Math.random() * 500);
      const backoff = Math.min(60000, 1500 * Math.pow(2, attempt) + jitter);
      const waitMs = parsedDelay ?? backoff;

      setLoadingText(`Rate-limited. Retrying in ${Math.ceil(waitMs / 1000)}s... (${attempt + 1})`);
      await sleep(waitMs);
      return generateReviewerWithRetry(payload, attempt + 1);
    }
  };

  const splitContentForRetry = (text) => {
    const src = String(text || "").trim();
    if (!src) return ["", ""];
    if (src.length < 2000) return [src, ""];

    const mid = Math.floor(src.length / 2);
    const paraBreak = src.lastIndexOf("\n\n", mid);
    const sentenceBreak = src.lastIndexOf(". ", mid);
    const cut = paraBreak > 1000 ? paraBreak : sentenceBreak > 1000 ? sentenceBreak + 1 : mid;
    return [src.slice(0, cut).trim(), src.slice(cut).trim()];
  };

  const mergeSplitQuizResults = (left, right) => ({
    summary: "",
    flashcards: [],
    quiz: [
      ...(Array.isArray(left?.quiz) ? left.quiz : []),
      ...(Array.isArray(right?.quiz) ? right.quiz : []),
    ],
  });

  const generateWithSplitFallback = async (payload, depth = 0) => {
    try {
      return await generateReviewerWithRetry(payload);
    } catch (err) {
      const status = extractStatus(err);
      const errText = extractErrText(err);
      const content = String(payload?.content || "");
      const canSplit = content.length >= 2500 && depth < 2;

      if (!isIncompleteOutputError(status, errText) || !canSplit) throw err;

      setLoadingText(`Batch incomplete. Splitting and retrying (${depth + 1})...`);
      const [leftText, rightText] = splitContentForRetry(content);
      if (!leftText || !rightText) throw err;

      const leftPayload = { ...payload, content: leftText };
      const rightPayload = { ...payload, content: rightText };

      const leftRes = await generateWithSplitFallback(leftPayload, depth + 1);
      const rightRes = await generateWithSplitFallback(rightPayload, depth + 1);
      return mergeSplitQuizResults(leftRes, rightRes);
    }
  };

  // Handlers
  const handleGenerate = async (activeTab) => {
    if (!user) return Swal.fire("Error", "Please login.", "error");
    const cleanTitle = String(title || "").trim();
    if (!cleanTitle) return Swal.fire("Error", "Please enter a topic.", "warning");
    if (cleanTitle.length > MAX_EXAM_NAME_LENGTH) {
      return Swal.fire(
        "Failed",
        `Exam name must not exceed ${MAX_EXAM_NAME_LENGTH} characters.`,
        "warning",
      );
    }

    setIsProcessing(true);
    setLoadingText("Reading files...");

    // ✅ DEBUGGING: Check Hard Mode Activation
    if (aiConfig.difficulty === 3) {
      console.warn("🔥 HARD MODE ACTIVATED: Expect strict choice balancing and tougher questions.");
    }

    try {
      const content =
        activeTab === "text" ? textInput : await processUploadedFiles(uploadedFiles);

      if (activeTab === "file") {
        const academicCheck = validateAcademicStudyMaterial(content);
        if (!academicCheck.isValid) {
          return Swal.fire({
            icon: "warning",
            title: academicCheck.title,
            text: academicCheck.message,
            confirmButtonColor: "#f59e0b",
          });
        }
      }

      const validation = validateStudyContent(content);
      if (!validation.isValid) {
        throw new Error(validation.message);
      }
      const normalized = String(content).trim();
      setRawContent(normalized);
      const shouldBatch =
        !!aiConfig.batchEnabled &&
        normalized.length >= Number(aiConfig.autoBatchThresholdChars || 18000);

      // BATCH MODE
      if (shouldBatch) {
        setLoadingText("Chunking content...");
        const targetMaxBatches = Number(aiConfig.targetMaxBatches || 24);
        const maxMaxChars = Number(aiConfig.maxMaxCharsPerBatch || 24000);
        let maxChars = Number(aiConfig.maxCharsPerBatch || 20000);

        let pack = prepareReviewerBatchPayloads(normalized, aiConfig, {
          maxCharsPerBatch: maxChars,
          overlapChars: Number(aiConfig.overlapChars || 1000),
          totalQuestionsMode: !!aiConfig.totalQuestionsMode,
          totalFlashcardsMode: false,
          maxQuestionsPerBatch: Number(aiConfig.maxQuestionsPerBatch || 60),
          maxFlashcardsPerBatch: 0,
        });

        while (pack.batchCount > targetMaxBatches && maxChars < maxMaxChars) {
          maxChars = Math.min(maxMaxChars, Math.floor(maxChars * 1.2));
          pack = prepareReviewerBatchPayloads(normalized, aiConfig, {
            maxCharsPerBatch: maxChars,
            overlapChars: Number(aiConfig.overlapChars || 1000),
            totalQuestionsMode: !!aiConfig.totalQuestionsMode,
            totalFlashcardsMode: false,
            maxQuestionsPerBatch: Number(aiConfig.maxQuestionsPerBatch || 60),
            maxFlashcardsPerBatch: 0,
          });
        }

        const { payloads, batchCount } = pack;

        if (batchCount > targetMaxBatches) {
          throw new Error(
            `Too many batches (${batchCount}). Increase maxCharsPerBatch or reduce overlapChars.`
          );
        }

        setLoadingText(`Prepared ${batchCount} batches. Starting AI...`);
        const batchResults = [];
        const throttleMs = Number(aiConfig.throttleMs || 0);

        for (let i = 0; i < payloads.length; i++) {
          setLoadingText(`Analyzing batch ${i + 1}/${batchCount} with AI...`);
          const res = await generateWithSplitFallback(payloads[i]);
          batchResults.push({
            ...res,
            batchMeta: payloads[i]?.batchMeta || { batchIndex: i, batchCount },
          });
          if (throttleMs > 0 && i < payloads.length - 1) await sleep(throttleMs);
        }

        const merged = mergeBatchOutputs(batchResults);
        const trimmed = trimMergedToTotals(merged);

        setGeneratedData(trimmed);
        setView("dashboard");
        return;
      }

      // SINGLE MODE
      setLoadingText("Generating Quiz...");
      const payload = prepareReviewerPayload(normalized, aiConfig);
      const result = await generateReviewerWithRetry(payload);

      result.summary = "";
      result.flashcards = [];
      result.quiz = normalizeQuizItems(result?.quiz);

      setGeneratedData(result);
      setView("dashboard");
    } catch (error) {
      const errText = extractErrText(error);
      if (error?.__dailyQuota) return Swal.fire("Quota reached", error.message, "warning");
      if (error?.__windowLimit) return Swal.fire("Rate limit reached", error.message, "warning");
      Swal.fire("Error", errText, "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!generatedData || !user) return;
    const cleanTitle = String(title || "").trim();
    if (!cleanTitle) return Swal.fire("Error", "Please enter a topic.", "warning");
    if (cleanTitle.length > MAX_EXAM_NAME_LENGTH) {
      return Swal.fire(
        "Failed",
        `Exam name must not exceed ${MAX_EXAM_NAME_LENGTH} characters.`,
        "warning",
      );
    }
    Swal.fire({ title: "Minting...", didOpen: () => Swal.showLoading() });

    try {
      const themeColors = ["blue", "orange", "purple", "emerald", "red"];
      const randomColor = themeColors[Math.floor(Math.random() * themeColors.length)];

      const payload = {
        // ✅ No need to send userId manually, backend uses cookie
        topic: cleanTitle,
        folder: folderId || null,
        color: randomColor,
        analyticsTopic: generatedData?.analyticsTopic || null,
        generatedData: {
          summary: "",
          flashcards: [],
          quiz: generatedData.quiz || [],
          analyticsTopic: generatedData?.analyticsTopic || null,
          batches: Array.isArray(generatedData?.batches) ? generatedData.batches : undefined,
        },
        config: aiConfig,
      };

      await saveReviewer(payload);
      Swal.fire({ icon: "success", title: "Minted Successfully!" });
    } catch (error) {
      Swal.fire("Error", extractErrText(error), "error");
    }
  };

  const handleDownload = () => generatePDFDocument({ title, generatedData, config: aiConfig });

  const updateGeneratedQuiz = (updaterOrList) => {
    setGeneratedData((prev) => {
      if (!prev) return prev;
      const nextQuiz =
        typeof updaterOrList === "function"
          ? updaterOrList(prev.quiz || [])
          : updaterOrList;
      return {
        ...prev,
        quiz: Array.isArray(nextQuiz) ? nextQuiz : [],
      };
    });
  };

  const resetForm = () => {
    setView("form");
    setGeneratedData(null);
    setTextInput("");
    setUploadedFiles([]);
    setRawContent("");
  };

  return {
    state: {
      user,
      folders,
      foldersLoading,
      view,
      isProcessing,
      loadingText,
      title,
      folderId,
      textInput,
      uploadedFiles,
      rawContent,
      generatedData,
      aiConfig,
    },
    actions: {
      setTitle,
      setFolderId,
      setTextInput,
      setUploadedFiles,
      updateConfig,
      updateGeneratedQuiz,
      handleGenerate,
      handleSave,
      handleDownload,
      resetForm,
    },
  };
};
